import React, { createContext, useContext, useReducer, useEffect } from "react";
import { authService } from "@/services/authService";
import { storage } from "@/utils/storage";
import { cacheManager } from "@/utils/cacheManager";
import { isTokenExpired, isTokenExpiringSoon } from "@/utils/tokenUtils";
import pushNotificationService from "@/services/pushNotificationService";
import {
  normalizeUserPermissions,
  serializePermissions,
  deserializePermissions,
  hasItemPermission,
  hasRoutePermission,
  hasModulePermission,
  getModuleItems,
  hasAnyPermission,
  hasAllPermissions,
  getModulePermissionLevels,
} from "@/utils/permissions";
import toast from "react-hot-toast";

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: null,
  pendingActions: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_START":
      return {
        ...state,
        isLoading: true,
      };
    case "LOGIN_SUCCESS": {
      const { user, token, refreshToken, permissions, pendingActions } =
        action.payload;
      console.log("🔄 LOGIN_SUCCESS reducer - received payload:", {
        user: user
          ? {
              id: user.id,
              email: user.email,
              first_name: user.first_name,
              last_name: user.last_name,
              UserType: user.UserType ? "Present" : "Missing",
              Role: user.Role ? "Present" : "Missing",
            }
          : "Missing",
        token: token ? "Present" : "Missing",
        permissions: permissions ? "Present" : "Missing",
        pendingActions: pendingActions
          ? `${pendingActions.total} actions`
          : "None",
      });
      return {
        ...state,
        user,
        token,
        refreshToken,
        permissions,
        pendingActions,
        isAuthenticated: true,
        isLoading: false,
      };
    }
    case "LOGIN_FAILURE":
      // Clear localStorage on login failure
      storage.clearAuthData();
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: null,
      };
    case "LOGOUT":
      // Clear localStorage on logout
      storage.clearAuthData();
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: null,
      };
    case "UPDATE_USER": {
      const updatedUser = { ...state.user, ...action.payload };
      // Don't store user data in localStorage - use token-based approach
      return {
        ...state,
        user: updatedUser,
      };
    }
    case "UPDATE_PERMISSIONS": {
      const permissions = normalizeUserPermissions(action.payload);
      // Don't store permissions in localStorage - use token-based approach
      return {
        ...state,
        permissions,
      };
    }
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // console.log('AuthProvider initial state:', state);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log("🔍 === AUTH CHECK START ===");
      const token = storage.getItem("token");
      console.log("🔍 Token exists:", !!token);

      // No token = not logged in
      if (!token) {
        console.log("❌ No token found");
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }

      // Token expired = logout
      const expired = isTokenExpired(token);
      console.log("🔍 Token expired:", expired);
      if (expired) {
        console.log("❌ Token expired, logging out");
        dispatch({ type: "LOGIN_FAILURE" });
        return;
      }

      console.log("✅ Token is valid, fetching user data...");

      // Token valid = fetch user data
      try {
        console.log("📞 Calling getCurrentUser...");
        const response = await authService.getCurrentUser();

        // Extract user from response.data.data.user (backend format)
        const user =
          response.data?.data?.user || response.data?.user || response.data;
        console.log("👤 Extracted user:", user);

        if (!user || !user.id) {
          console.error("❌ User validation failed:", {
            user,
            hasId: !!user?.id,
          });
          throw new Error("Invalid user data received from server");
        }

        console.log("✅ User validated successfully:", user.id, user.email);

        // Get user type and permissions
        let permissions = null;

        // First, try to get permissions from the user data
        if (user?.UserType?.sidebar_modules) {
          console.log("✅ Using UserType from getCurrentUser response");
          permissions = normalizeUserPermissions(user.UserType.sidebar_modules);
        } else if (user?.user_type_id) {
          // Fetch user type data separately using token
          console.log(
            "🔄 Fetching user type data for user_type_id:",
            user.user_type_id,
          );
          try {
            const userTypeResponse = await fetch(
              `${import.meta.env.VITE_API_URL}/user-types/${user.user_type_id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            const userTypeData = await userTypeResponse.json();
            console.log("📞 User type response:", userTypeData);

            if (
              userTypeData.success &&
              userTypeData.data.userType.sidebar_modules
            ) {
              permissions = normalizeUserPermissions(
                userTypeData.data.userType.sidebar_modules,
              );

              // Enhance user object with UserType data
              user.UserType = userTypeData.data.userType;
              console.log("✅ Enhanced user with UserType data");
            }
          } catch (permError) {
            console.warn("⚠️ Failed to load user type data:", permError);
          }
        }

        console.log(
          "📊 Final permissions:",
          permissions ? "Generated" : "Missing",
        );
        if (permissions) {
          console.log(
            "📊 Available modules:",
            Object.keys(permissions.modules || {}).join(", "),
          );
        }

        console.log("✅ Authentication successful, dispatching LOGIN_SUCCESS");
        console.log("👤 User object being dispatched:", {
          id: user.id,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          UserType: user.UserType
            ? {
                name: user.UserType.name,
                sidebar_modules: user.UserType.sidebar_modules
                  ? "Present"
                  : "Missing",
              }
            : "Missing",
          Role: user.Role ? { name: user.Role.name } : "Missing",
        });

        // Get refresh token from storage
        const refreshToken = storage.getItem("refreshToken");

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: {
            user,
            token,
            refreshToken,
            permissions,
          },
        });
      } catch (error) {
        console.error("❌ Failed to fetch user data:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });

        // Only logout on auth errors (401/403)
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.log("❌ Auth error (401/403), logging out");
          dispatch({ type: "LOGIN_FAILURE" });
        } else {
          // For any other error, just stop loading but DON'T logout
          // Keep the token so user can try again
          console.log("⚠️ Error fetching user data, but keeping token");
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      dispatch({ type: "LOGIN_START" });
      const response = await authService.login(credentials);

      if (response.data && response.data.success) {
        const { user, accessToken, refreshToken, pendingActions } =
          response.data.data;
        storage.setItem("token", accessToken);

        // Store refresh token if provided
        if (refreshToken) {
          storage.setItem("refreshToken", refreshToken);
        }

        // Load user permissions from the login response or fetch separately
        let permissions = null;

        // First, try to get permissions from the user data in login response
        if (user?.UserType?.sidebar_modules) {
          console.log(
            "AuthContext - Using permissions from login response:",
            user.UserType.sidebar_modules,
          );
          permissions = normalizeUserPermissions(user.UserType.sidebar_modules);
          storage.setItem("permissions", permissions);
        } else if (user?.user_type_id) {
          // Fallback: fetch user type data separately
          try {
            console.log(
              "AuthContext - Fetching user type data for user_type_id:",
              user.user_type_id,
            );
            const userTypeResponse = await fetch(
              `${import.meta.env.VITE_API_URL}/user-types/${user.user_type_id}`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              },
            );

            const userTypeData = await userTypeResponse.json();
            console.log("AuthContext - User type response:", userTypeData);

            if (
              userTypeData.success &&
              userTypeData.data.userType.sidebar_modules
            ) {
              permissions = normalizeUserPermissions(
                userTypeData.data.userType.sidebar_modules,
              );
              storage.setItem("permissions", permissions);
            }
          } catch (permError) {
            console.warn(
              "Failed to load user permissions during login:",
              permError,
            );
          }
        }

        console.log(
          "📊 Final permissions after login:",
          permissions ? "Generated" : "Missing",
        );
        if (permissions) {
          console.log(
            "📊 Available modules:",
            Object.keys(permissions.modules || {}).join(", "),
          );
        }

        // Cache user data and permissions for offline use
        storage.setItem("user", JSON.stringify(user));
        if (permissions) {
          // Serialize permissions (convert Set to Array) before storing
          storage.setItem(
            "permissions",
            JSON.stringify(serializePermissions(permissions)),
          );
        }

        // Store refresh token if provided
        if (refreshToken) {
          storage.setItem("refreshToken", refreshToken);
        }

        // Clear service worker caches to ensure fresh data after login
        console.log("🔄 Clearing service worker caches after login...");
        cacheManager
          .clearRuntimeCaches()
          .catch((err) => console.warn("Failed to clear caches:", err));

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: {
            user,
            token: accessToken,
            refreshToken,
            permissions,
            pendingActions,
          },
        });

        toast.success("Login successful!");
        return { success: true, user, pendingActions }; // Return user data and pending actions
      } else {
        throw new Error(response.data?.message || "Login failed");
      }
    } catch (error) {
      dispatch({ type: "LOGIN_FAILURE" });
      const message =
        error.response?.data?.message || error.message || "Login failed";
      toast.error(message);
      return { success: false, message };
    }
  };

  const refreshAuthToken = async () => {
    try {
      const refreshToken = storage.getItem("refreshToken");
      if (!refreshToken || isTokenExpired(refreshToken)) {
        throw new Error("No valid refresh token");
      }

      const response = await authService.refreshToken(refreshToken);
      const newToken = response.data.token || response.data.accessToken;
      const newRefreshToken = response.data.refreshToken;

      if (newToken) {
        storage.setItem("token", newToken);
        if (newRefreshToken) {
          storage.setItem("refreshToken", newRefreshToken);
        }
        return newToken;
      }

      throw new Error("No token in refresh response");
    } catch (error) {
      console.error("Token refresh failed:", error);
      dispatch({ type: "LOGOUT" });
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      toast.success(
        "Registration successful! Please check your email for verification.",
      );
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Registration failed";
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      // Unsubscribe this device from push notifications before clearing auth
      // state (best-effort - must never block logout if it fails).
      await pushNotificationService.unsubscribe().catch((error) => {
        console.error("Push unsubscribe error during logout:", error);
      });

      // Call backend logout endpoint
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      console.log("🚪 Starting comprehensive logout process...");

      // Clear ALL browser storage (localStorage, sessionStorage, cookies, IndexedDB, caches, SW)
      await storage.clearAllStorage();

      // Update auth state
      dispatch({ type: "LOGOUT" });

      // Show success message
      toast.success("Logged out successfully");

      // Force a hard reload to ensure clean state
      // Use setTimeout to allow toast to show
      setTimeout(() => {
        console.log("🔄 Forcing hard reload after logout...");
        window.location.href = "/login";
        window.location.reload(true);
      }, 500);
    }
  };

  const updateUser = (userData) => {
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  // Permission checking functions
  const hasPermission = (permissionId) => {
    if (!state.permissions || !permissionId) {
      return false;
    }
    return hasItemPermission(state.permissions, permissionId);
  };

  const hasRoute = (route) => {
    if (!state.permissions || !route) {
      return false;
    }
    return hasRoutePermission(state.permissions, route);
  };

  const hasModule = (moduleId) => {
    if (!state.permissions || !moduleId) {
      return false;
    }
    return hasModulePermission(state.permissions, moduleId);
  };

  const getModulePermissions = (moduleId) => {
    if (!state.permissions || !moduleId) {
      return [];
    }
    return getModuleItems(state.permissions, moduleId);
  };

  const hasAnyOf = (permissionIds) => {
    if (!state.permissions || !Array.isArray(permissionIds)) {
      return false;
    }
    return hasAnyPermission(state.permissions, permissionIds);
  };

  const hasAllOf = (permissionIds) => {
    if (!state.permissions || !Array.isArray(permissionIds)) {
      return false;
    }
    return hasAllPermissions(state.permissions, permissionIds);
  };

  const getPermissionLevels = (moduleId) => {
    if (!state.permissions || !moduleId) {
      return [];
    }
    return getModulePermissionLevels(state.permissions, moduleId);
  };

  const hasRole = (role) => {
    if (!state.user) return false;
    const userType = state.user.UserType || state.user.user_type;
    return userType && userType.name === role;
  };

  const hasAnyRole = (roles) => {
    return roles.some((role) => hasRole(role));
  };

  const updateUserPermissions = async (userId) => {
    try {
      if (state.user?.user_type_id) {
        console.log(
          "Updating user permissions for user_type_id:",
          state.user.user_type_id,
        );
        const userTypeResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/user-types/${state.user.user_type_id}`,
          {
            headers: {
              Authorization: `Bearer ${state.token}`,
            },
          },
        );

        const userTypeData = await userTypeResponse.json();
        console.log("Update permissions response:", userTypeData);

        if (
          userTypeData.success &&
          userTypeData.data.userType.sidebar_modules
        ) {
          dispatch({
            type: "UPDATE_PERMISSIONS",
            payload: userTypeData.data.userType.sidebar_modules,
          });
        }
      }
    } catch (error) {
      console.error("Failed to update user permissions:", error);
    }
  };

  const value = {
    ...state,
    dispatch, // Export dispatch for direct state updates
    login,
    register,
    logout,
    updateUser,
    updateUserPermissions,
    refreshAuthToken,
    hasPermission,
    hasRoute,
    hasModule,
    getModulePermissions,
    hasAnyOf,
    hasAllOf,
    getPermissionLevels,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
